import { ReactElement, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '~/behaviors/redux';
import { accountSelectors } from '~/behaviors/selectors/account.selectors';
import { urls } from '~/tools/urls';
import { getLocalItem, removeLocalItem } from '~/adapters/appStorage';
import { zendeskEnvironmentUrl } from '~/config/settings/zendesk';
import { isTrustedExternalUrl } from './CheckAuth.helpers';

const CheckAuth = ({ children }: { children: ReactElement }) => {
  const location = useLocation();
  const queryParams = urls.parseQueryString(location.search, { comma: false });

  const account = useAppSelector(accountSelectors.selectAccount());
  // @ts-ignore
  const isLoggedIn = account.userId !== null;
  // Using a ref to avoid re-rendering the component when the redirect happens
  // This is only necessary for dev because of React's strict mode, because useEffect is called twice in dev mode
  const redirectedRef = useRef<boolean>(false);
  const redirectUrl = queryParams.redirectTo || getLocalItem('redirectTo') || '/';

  useEffect(() => {
    return () => {
      if (redirectedRef.current) {
        removeLocalItem('redirectTo');
      }
    };
  }, []);

  // External redirects are only allowed to explicitly trusted hosts (e.g. Zendesk SSO)
  if (isTrustedExternalUrl(redirectUrl, [zendeskEnvironmentUrl])) {
    return (window.location.href = redirectUrl);
  }

  const url = urls.isSafeRelativePath(redirectUrl) ? redirectUrl : '/';

  if (isLoggedIn) {
    redirectedRef.current = true;
    return <Navigate to={url} replace />;
  }

  return children;
};

export { CheckAuth };
